import { yupResolver } from '@hookform/resolvers/yup';
import { kebabCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { InferType } from 'yup';

import { FormError } from '../../../../../data/utils/form.utils';
import { schemas } from './schemas';

export enum ProfileFormAction {
  DEFAULT = 'DEFAULT',
  UPDATEPASSWORD = 'UPDATE_PASSWORD',
  UPDATEPINCODE = 'UPDATE_PINCODE',
}

export function useProfileForm(action: ProfileFormAction) {
  const { schema, values, resolver } = useMemo(() => {
    if (action === ProfileFormAction.UPDATEPASSWORD) {
      return {
        schema: schemas.password,
        values: schemas.password.getDefault(),
        resolver: yupResolver(schemas.password),
      };
    }

    if (action === ProfileFormAction.UPDATEPINCODE) {
      return {
        schema: schemas.pincode,
        values: schemas.pincode.getDefault(),
        resolver: yupResolver(schemas.pincode),
      };
    }

    return {
      schema: schemas.default,
      values: schemas.default.getDefault(),
      resolver: yupResolver(schemas.default),
    };
  }, [action]);

  const form = useForm<InferType<typeof schema>>({
    resolver: resolver as Resolver<InferType<typeof schema>>,
    defaultValues: values,
    mode: 'onBlur',
  });

  const intl = useIntl();

  const setErrors = useCallback(
    (errors: FormError[] | null | undefined) => {
      if (errors) {
        errors.forEach(error => {
          const name = error.fieldName as keyof InferType<typeof schema>;

          form.setError(name, {
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(error.message)}`,
              defaultMessage: error.message,
            }),
          });
        });

        return false;
      }

      return true;
    },
    [form, intl]
  );

  return {
    form,
    formIsValid: useMemo(() => form.formState.isValid, [form.formState]),
    schema,
    setErrors,
  };
}
